import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WompiWebhookService } from '../../../services/wompi-webhook.service';
import { WompiWebhookRequestDetail, WompiRequestStatus, ProcessingLogEntry } from '../../../interfaces/wompi-webhook.interfaces';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-view-wompi-request',
  templateUrl: './view-wompi-request.component.html',
  styleUrls: ['./view-wompi-request.component.scss']
})
export class ViewWompiRequestComponent implements OnInit {
  request: WompiWebhookRequestDetail | null = null;
  loading = false;
  rawWebhookDataFormatted: string = '';
  showRawData = false;
  httpRequestInfoFormatted: string = '';
  showHttpRequestInfo = false;
  httpRequestData: any = null;
  processingLogs: ProcessingLogEntry[] = [];
  showProcessingLogs = true;
  
  WompiRequestStatus = WompiRequestStatus;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private wompiService: WompiWebhookService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadRequest(parseInt(id, 10));
    }
  }

  loadRequest(id: number): void {
    this.loading = true;
    this.wompiService.getRequestById(id).subscribe({
      next: (data) => {
        this.request = data;
        if (data.rawWebhookData) {
          try {
            const parsed = JSON.parse(data.rawWebhookData);
            this.rawWebhookDataFormatted = JSON.stringify(parsed, null, 2);
          } catch (e) {
            this.rawWebhookDataFormatted = data.rawWebhookData;
          }
        }
        
        if (data.httpRequestInfo) {
          try {
            this.httpRequestData = JSON.parse(data.httpRequestInfo);
            this.httpRequestInfoFormatted = JSON.stringify(this.httpRequestData, null, 2);
          } catch (e) {
            this.httpRequestInfoFormatted = data.httpRequestInfo;
          }
        }
        
        // Parsear los logs de procesamiento
        if (data.processingLogs) {
          try {
            this.processingLogs = JSON.parse(data.processingLogs);
          } catch (e) {
            this.processingLogs = [];
          }
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error cargando solicitud:', error);
        this.toastService.showError('Error cargando detalle de la solicitud');
        this.loading = false;
        this.router.navigate(['/wompi-requests']);
      }
    });
  }

  retryRequest(): void {
    if (!this.request) return;

    if (this.request.status === WompiRequestStatus.Success) {
      this.toastService.showWarning('Esta solicitud ya fue procesada exitosamente');
      return;
    }

    if (this.request.status === WompiRequestStatus.Processing) {
      this.toastService.showWarning('Esta solicitud está siendo procesada actualmente');
      return;
    }

    if (confirm(`¿Está seguro de reprocesar la solicitud de transacción: ${this.request.transactionId}?\n\nAsegúrese de haber corregido el problema antes de reprocesar.`)) {
      this.wompiService.retryProcessing(this.request.id).subscribe({
        next: (response) => {
          this.toastService.showSuccess(response.message);
          this.loadRequest(this.request!.id);
        },
        error: (error) => {
          console.error('Error reprocesando solicitud:', error);
          this.toastService.showError(error.error || 'Error al reprocesar la solicitud');
        }
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/wompi-requests']);
  }

  getStatusName(status: WompiRequestStatus): string {
    return this.wompiService.getStatusName(status);
  }

  getStatusBadgeClass(status: WompiRequestStatus): string {
    return this.wompiService.getStatusBadgeClass(status);
  }

  getStatusIcon(status: WompiRequestStatus): string {
    return this.wompiService.getStatusIcon(status);
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES');
  }

  formatAmount(amountInCents: number, currency?: string): string {
    return this.wompiService.formatAmount(amountInCents, currency || 'COP');
  }

  toggleRawData(): void {
    this.showRawData = !this.showRawData;
  }

  toggleHttpRequestInfo(): void {
    this.showHttpRequestInfo = !this.showHttpRequestInfo;
  }

  toggleProcessingLogs(): void {
    this.showProcessingLogs = !this.showProcessingLogs;
  }

  getLogLevelClass(level: string): string {
    switch (level) {
      case 'INF': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'WRN': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'ERR': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  }

  getLogLevelIcon(level: string): string {
    switch (level) {
      case 'INF': return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
      case 'WRN': return 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z';
      case 'ERR': return 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z';
      default: return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
    }
  }

  formatLogTimestamp(timestamp: string): string {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      fractionalSecondDigits: 3 
    });
  }

  getLogCountByLevel(level: string): number {
    return this.processingLogs.filter(l => l.Level === level).length;
  }

  getHttpHeaders(): { key: string, value: string }[] {
    if (!this.httpRequestData?.Headers) return [];
    
    return Object.entries(this.httpRequestData.Headers).map(([key, value]) => ({
      key,
      value: value as string
    }));
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.toastService.showSuccess('Copiado al portapapeles');
    }).catch(err => {
      console.error('Error copiando al portapapeles:', err);
      this.toastService.showError('Error al copiar');
    });
  }

  viewPaymentLink(): void {
    if (this.request?.paymentLinkId_Internal) {
      // Navegar a la vista de payment link si existe
      this.toastService.showSuccess(`Payment Link ID: ${this.request.paymentLinkId_Internal}`);
    }
  }

  viewEnrollment(): void {
    if (this.request?.enrollmentId) {
      this.router.navigate(['/enrollments', this.request.enrollmentId]);
    }
  }

  viewParticipant(): void {
    if (this.request?.participantId) {
      this.router.navigate(['/participants', this.request.participantId]);
    }
  }

  getTransactionStatusColor(status: string): string {
    return this.wompiService.getTransactionStatusColor(status);
  }

  getPaymentMethodIcon(paymentMethodType: string): string {
    return this.wompiService.getPaymentMethodIcon(paymentMethodType);
  }

  formatProcessingTime(timeMs: number | null | undefined): string {
    if (!timeMs) return '-';
    
    if (timeMs < 1000) {
      return `${timeMs}ms`;
    } else if (timeMs < 60000) {
      return `${(timeMs / 1000).toFixed(1)}s`;
    } else {
      const minutes = Math.floor(timeMs / 60000);
      const seconds = Math.floor((timeMs % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
  }
}
